/* import __COLOCATED_TEMPLATE__ from './file-upload-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type FileSource from 'embercom/models/content-service/file-source';
import EmbercomFileUploader from 'embercom/lib/articles/embercom-file-uploader';
import ENV from 'embercom/config/environment';
// @ts-ignore
import { ref } from 'ember-ref-bucket';
import type Store from '@ember-data/store';
import { FIN_SUPPORTED_LANGUAGES, type Language } from 'embercom/lib/ai-content-library/constants';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { task } from 'ember-concurrency-decorators';
import type ContentImportService from 'embercom/services/content-import-service';
import { post } from 'embercom/lib/ajax';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';

interface Args {
  modal: any;
  fileSource?: FileSource;
  onModalClose: () => void;
  onNavigationBack?: () => void;
  context?: {
    onUploadSuccess?: () => void;
    onReuploadSuccess?: () => void;
    folder?: string;
  };
}

interface Upload {
  upload_id: number | string;
  file_name: string;
  file_type: string;
  locale: string;
  folder_id?: string;
}

interface DropDownOption {
  text: string;
  value: string;
}

const MAX_FILE_SIZE_IN_MB = 45;
const MAX_FILE_SIZE_IN_MB_WITH_FEATURE_FLAG = 100;
const MAX_FILE_COUNT = 10;
const MAX_FILE_COUNT_WITH_FEATURE_FLAG = 100;

export default class FileUploadModal extends Component<Args> {
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare notificationsService: any;
  @service declare contentImportService: ContentImportService;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare unifiedContentState: UnifiedContentStateService;

  @tracked fileUploadError?: string;
  @tracked uploadState = 0;
  @tracked uploading = false;
  @tracked files: File[] = [];

  @tracked locale = undefined;
  @tracked isDragging = false;

  @ref('fileInput') fileInput?: HTMLInputElement;

  constructor(owner: unknown, args: any) {
    super(owner, args);
  }

  get app() {
    return this.appService.app;
  }

  get fileSizeLimit() {
    return this.appService.app.canUseFeature('team-knowledge-foundations-file-upload-size')
      ? MAX_FILE_SIZE_IN_MB_WITH_FEATURE_FLAG
      : MAX_FILE_SIZE_IN_MB;
  }

  get maxFileCount() {
    if (this.args.fileSource) {
      return 1;
    }

    if (!this.appService.app.canUseFeature('team-knowledge-foundations-multi-file-upload')) {
      return 1;
    }

    return this.appService.app.canUseFeature('team-knowledge-foundations-bulk-upload-limit-100')
      ? MAX_FILE_COUNT_WITH_FEATURE_FLAG
      : MAX_FILE_COUNT;
  }

  @action
  async upload() {
    this.uploading = true;
    this.trackAnalyticsEvent(
      'clicked',
      this.args.fileSource ? 'reupload_button' : 'upload_button',
      {
        fileSource: this.args.fileSource,
      },
    );

    let uploadPromises = this.files.map((file) => taskFor(this.uploadFile).perform(file));
    let uploads = await Promise.all(uploadPromises);
    let successfulUploads = uploads.filter((u) => u) as Upload[];
    try {
      if (successfulUploads.length > 0) {
        if (this.args.fileSource) {
          await this.updateFileSourceRecord(successfulUploads[0]);
          this.args.context?.onReuploadSuccess?.();
        } else {
          await this.saveFileSourceRecords(successfulUploads);
          this.onUploadSuccess();
        }
      }
    } catch (e) {
      if (e.jqXHR.responseJSON && e.jqXHR.responseJSON.errors) {
        this.notificationsService.notifyError(e.jqXHR.responseJSON.errors[0].message);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-content-library.file-content.new-content-modal.file-source-save-error'),
        );
      }
    }
    this.uploading = false;
  }

  @task({ maxConcurrency: 3, enqueue: true })
  *uploadFile(file: any): TaskGenerator<Upload | null> {
    try {
      let upload: Upload = yield this.postFile(file);
      return upload;
    } catch (error) {
      console.error('Error uploading file', file.name, error);
      this.notificationsService.notifyError(
        this.intl.t('ai-content-library.file-content.new-content-modal.upload-error-for-file', {
          fileName: file.name,
        }),
      );
      return null;
    }
  }

  @action
  onUploadSuccess() {
    this.args.context?.onUploadSuccess?.();
    this.args.onModalClose();
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-content-library.file-content.new-content-modal.success'),
    );
    this.contentImportService.showFinIngestionJobStatusBanner();
    if (this.appService.app.canUseUnifiedContentUI) {
      this.unifiedContentState.refreshContent();
    }
  }

  get aFileIsTooLarge() {
    return this.files.some((file) => this.fileIsTooLarge(file));
  }

  get anInvalidFileExtension() {
    return this.files.some((file) => !this.fileHasValidExtension(file));
  }

  @action
  errorMessage(file: File): string {
    if (this.fileIsTooLarge(file)) {
      return this.intl.t('ai-content-library.file-content.new-content-modal.file-size-error', {
        maxFileSize: this.fileSizeLimit,
      });
    }
    if (!this.fileHasValidExtension(file)) {
      return this.intl.t('ai-content-library.file-content.new-content-modal.file-type-error');
    }
    return '';
  }

  fileHasValidExtension(file: File) {
    let fileName = file?.name;
    if (!fileName) {
      return false;
    }

    let regexp = new RegExp(`\\.(pdf|docx)$`, 'i');
    return fileName.match(regexp);
  }

  fileIsTooLarge(file: File) {
    if (!file) {
      return false;
    }
    return file.size > this.fileSizeLimit * 1000000;
  }

  addFiles(newFiles: FileList | File[]) {
    let filesArray = Array.from(newFiles);
    // If maxFileCount is 1, replace existing files instead of adding => Mimics previous behavior
    if (this.maxFileCount === 1) {
      this.files = [filesArray[0]];
      return;
    }

    let availableSlots = this.maxFileCount - this.files.length;
    let filesToAdd = filesArray.slice(0, availableSlots);

    this.files = [...this.files, ...filesToAdd];

    if (filesArray.length > availableSlots && availableSlots > 0) {
      this.notificationsService.notifyError(
        this.intl.t('ai-content-library.file-content.new-content-modal.some-files-not-added', {
          maxFiles: this.maxFileCount,
        }),
      );
    } else if (availableSlots === 0) {
      this.notificationsService.notifyError(
        this.intl.t('ai-content-library.file-content.new-content-modal.max-files-reached', {
          maxFiles: this.maxFileCount,
        }),
      );
    }
  }

  @action
  pickFile() {
    if (ENV.environment === 'test') {
      return this.addFiles([{ name: 'test.csv' } as File]);
    }
    this.fileInput?.click();
  }

  @action
  removeFile(fileToRemove: File) {
    this.files = this.files.filter((file) => file !== fileToRemove);
  }

  @action
  dragEnter(event: any) {
    event.preventDefault();
    this.isDragging = true;
  }

  @action
  dragOver(event: any) {
    this.dragEnter(event);
  }

  @action
  dragLeave(event: any) {
    event.preventDefault();
    this.isDragging = false;
  }

  @action
  drop(event: any) {
    event.preventDefault();
    this.addFiles(event.dataTransfer.files);
  }

  get canUseFileLangDetect() {
    return (
      this.appService.app.canUseFeature('team-knowledge-foundations-file-lang-detect') ||
      this.appService.app.canUseFeature('team-knowledge-foundations-multi-file-upload')
    );
  }

  async postFile(file: any): Promise<Upload> {
    if (!this.locale && !this.canUseFileLangDetect) {
      this.notificationsService.notifyError();
      throw new Error('Locale not selected');
    }

    let uploader = new EmbercomFileUploader(file, {
      policyUrl: `/apps/${this.appService.app.id}/uploads`,
    });
    let response = await uploader.upload();

    if (response.type === 'failure') {
      this.notificationsService.notifyError(response.reason);
      throw new Error(response.reason);
    }

    let upload: Upload = {
      upload_id: uploader.policy.id,
      file_type: file.type,
      file_name: file.name,
      locale: this.locale || 'en',
    };

    if (this.args.context?.folder) {
      upload.folder_id = this.args.context.folder;
    }

    return upload;
  }

  async updateFileSourceRecord(upload: Upload) {
    let fileSource = this.args.fileSource;
    if (!fileSource) {
      return;
    }
    fileSource.setProperties({
      fileName: upload.file_name,
      fileType: upload.file_type,
      uploadId: upload.upload_id,
      locale: upload.locale,
    });
    await fileSource.save();
  }

  async saveFileSourceRecords(uploads: Upload[]) {
    if (uploads.length === 1) {
      // Single file: use original API endpoint and body structure
      let upload = uploads[0];
      let fileSource = this.store.createRecord('content-service/file-source', {
        fileName: upload.file_name,
        fileType: upload.file_type,
        uploadId: upload.upload_id,
        locale: upload.locale,
        folderId: upload.folder_id,
      });
      await fileSource.save();
    } else {
      let files = uploads.map((upload) => ({
        file_name: upload.file_name,
        file_type: upload.file_type,
        upload_id: upload.upload_id,
      }));

      await post('/ember/content_service/file_sources/bulk_create', {
        files,
        folder_id: this.args.context?.folder,
        app_id: this.appService.app.id,
      });
    }
  }

  @action
  onFileChange(event: Event) {
    let target = event.target as HTMLInputElement;
    if (target.files) {
      this.addFiles(target.files);
    }
  }

  get disableUpload(): boolean {
    return (
      this.uploading ||
      this.files.length === 0 ||
      this.files.length > this.maxFileCount ||
      this.aFileIsTooLarge ||
      this.anInvalidFileExtension ||
      !(this.locale || this.canUseFileLangDetect)
    );
  }

  get languages(): Array<DropDownOption> {
    return FIN_SUPPORTED_LANGUAGES.map((language: Language) => {
      return {
        text: language.name,
        value: language.locale,
      };
    });
  }

  private trackAnalyticsEvent(action: string, object: string, metadata?: any): void {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      section: 'operator',
      context: 'file_upload_modal',
      place: 'fin_content_page',
      ...metadata,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'ContentService::AiContentLibrary::FileUploadModal': typeof FileUploadModal;
  }
}
